// Fase 17.3, Plano 05 — filtros da lista "Roteiros das peças" (D-13): leitura/escrita na URL e rótulos pt-BR.
// Funções puras: quem filtra, ordena e pagina é o servidor (GET /api/v1/roteiros/pecas).
import type { FiltrosPecasRoteiroDto, SituacaoRoteiroPeca } from '@pieltec/contracts';

export const TAMANHO_PAGINA_PADRAO = 50;

export const ROTULOS_SITUACAO: Readonly<Record<SituacaoRoteiroPeca, string>> = {
  SEM_ROTEIRO: 'Sem roteiro',
  IMPORTADO: 'Importado do CargaMáquina',
  AJUSTADO: 'Ajustado no sistema',
};

const SITUACOES: readonly SituacaoRoteiroPeca[] = ['SEM_ROTEIRO', 'IMPORTADO', 'AJUSTADO'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function inteiro(valor: string | null, minimo: number, maximo: number): number | undefined {
  if (valor === null || !/^\d+$/.test(valor)) return undefined;
  const numero = Number(valor);
  return numero >= minimo && numero <= maximo ? numero : undefined;
}

/** Lê os filtros da query string; valores inválidos são ignorados. `lote` é apelido de `loteId` (link da Central de Pendências). */
export function filtrosDaUrl(search: string): FiltrosPecasRoteiroDto {
  const params = new URLSearchParams(search);
  const texto = (chave: string): string | undefined => {
    const valor = params.get(chave)?.trim();
    return valor ? valor.slice(0, 100) : undefined;
  };
  const situacao = params.get('situacao');
  const modeloId = params.get('modeloId');
  const loteId = params.get('loteId') ?? params.get('lote');
  const pagina = inteiro(params.get('pagina'), 1, 1_000_000);
  const tamanhoPagina = inteiro(params.get('tamanhoPagina'), 10, 100);
  return {
    ...(texto('clienteId') && UUID.test(texto('clienteId') ?? '') ? { clienteId: texto('clienteId') } : {}),
    ...(params.get('catalogo') === 'true' ? { catalogo: true } : {}),
    ...(texto('busca') ? { busca: texto('busca') } : {}),
    ...(situacao && (SITUACOES as readonly string[]).includes(situacao) ? { situacao: situacao as SituacaoRoteiroPeca } : {}),
    ...(texto('cliente') ? { cliente: texto('cliente') } : {}),
    ...(modeloId && UUID.test(modeloId) ? { modeloId } : {}),
    ...(params.get('incompletos') === 'true' ? { incompletos: true } : {}),
    ...(params.get('aguardandoRoteiro') === 'true' ? { aguardandoRoteiro: true } : {}),
    ...(params.get('semTintaDaPeca') === 'true' ? { semTintaDaPeca: true } : {}),
    ...(loteId && UUID.test(loteId) ? { loteId } : {}),
    ...(pagina && pagina > 1 ? { pagina } : {}),
    ...(tamanhoPagina && tamanhoPagina !== TAMANHO_PAGINA_PADRAO ? { tamanhoPagina } : {}),
  };
}

/** Query string (sem `?`) só com os filtros ativos, na ordem fixa dos contratos. */
export function urlDosFiltros(filtros: FiltrosPecasRoteiroDto): string {
  const params = new URLSearchParams();
  if (filtros.clienteId) params.set('clienteId', filtros.clienteId);
  if (filtros.catalogo) params.set('catalogo', 'true');
  const busca = filtros.busca?.trim();
  if (busca) params.set('busca', busca);
  if (filtros.situacao) params.set('situacao', filtros.situacao);
  const cliente = filtros.cliente?.trim();
  if (cliente) params.set('cliente', cliente);
  if (filtros.modeloId) params.set('modeloId', filtros.modeloId);
  if (filtros.incompletos) params.set('incompletos', 'true');
  if (filtros.aguardandoRoteiro) params.set('aguardandoRoteiro', 'true');
  if (filtros.semTintaDaPeca) params.set('semTintaDaPeca', 'true');
  if (filtros.loteId) params.set('loteId', filtros.loteId);
  if (filtros.pagina && filtros.pagina > 1) params.set('pagina', String(filtros.pagina));
  if (filtros.tamanhoPagina && filtros.tamanhoPagina !== TAMANHO_PAGINA_PADRAO) params.set('tamanhoPagina', String(filtros.tamanhoPagina));
  return params.toString();
}

/** Aplica uma mudança; qualquer alteração de filtro (exceto a própria página) volta para a página 1. */
export function aplicarFiltros(atual: FiltrosPecasRoteiroDto, mudanca: Partial<FiltrosPecasRoteiroDto>): FiltrosPecasRoteiroDto {
  const combinado = { ...atual, ...mudanca };
  if ('pagina' in mudanca) return combinado;
  const { pagina: _descartada, ...semPagina } = combinado;
  void _descartada;
  return semPagina;
}

/** Há algum filtro além da paginação? */
export function haFiltrosAtivos(filtros: FiltrosPecasRoteiroDto): boolean {
  return Boolean(
    filtros.busca?.trim() || filtros.situacao || filtros.cliente?.trim() || filtros.clienteId || filtros.modeloId ||
      filtros.incompletos || filtros.aguardandoRoteiro || filtros.semTintaDaPeca || filtros.loteId,
  );
}
